import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Form, Input } from "antd";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { apiFetch } from "../../api/client";
import type { DeviseStatusResponse } from "./PublicPage";
import { PublicPage, StatusAlert } from "./PublicPage";

interface ResetForm {
  password: string;
  password_confirmation: string;
}

/**
 * /users/password/edit?reset_password_token=… — where the link in the reset mail
 * lands. SpeechplanMailer builds that URL (spa_reset_password_url); Devise's own
 * helper would point at the API, which has no form to offer.
 *
 * The token is spent by this request: the API deliberately does not sign the
 * user in afterwards, so the login screen is the next step either way.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("reset_password_token");

  const reset = useMutation({
    mutationFn: (values: ResetForm) =>
      apiFetch<DeviseStatusResponse>("/password", {
        method: "PUT",
        anonymous: true,
        body: {
          user: {
            reset_password_token: token,
            password: values.password,
            password_confirmation: values.password_confirmation,
          },
        },
      }),
  });

  return (
    <PublicPage title={t("auth.views.passwords.change_your_password")}>
      {token ? (
        <>
          <StatusAlert result={reset.data} error={reset.error} />

          {reset.isSuccess ? (
            <Link to="/login">{t("auth.sign_in")}</Link>
          ) : (
            <Form<ResetForm> layout="vertical" onFinish={(values) => reset.mutate(values)}>
              <Form.Item
                name="password"
                label={t("activerecord.attributes.user.password")}
                rules={[{ required: true }]}
              >
                <Input.Password autoComplete="new-password" autoFocus />
              </Form.Item>

              <Form.Item
                name="password_confirmation"
                label={t("activerecord.attributes.user.password_confirmation")}
                dependencies={["password"]}
                rules={[
                  { required: true },
                  ({ getFieldValue }) => ({
                    validator: (_rule, value) =>
                      !value || value === getFieldValue("password")
                        ? Promise.resolve()
                        : Promise.reject(new Error(t("errors.messages.confirmation"))),
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>

              <Button type="primary" htmlType="submit" block loading={reset.isPending}>
                {t("auth.views.passwords.change")}
              </Button>
            </Form>
          )}
        </>
      ) : (
        // Reached without the token: the link was truncated by the mail client,
        // which is exactly the case devise.passwords.no_token describes.
        <Alert type="warning" showIcon title={t("devise.passwords.no_token")} />
      )}
    </PublicPage>
  );
}
